import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  Grid,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import NumericFormat from 'react-number-format';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import api from '../../services/api';

const schema = yup.object().shape({
  nome: yup.string().required('Nome é obrigatório'),
});

const convertBrNumberToDb = (value) => {
  if (!value) return null;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    return parseFloat(value.replace(/\./g, '').replace(',', '.')) || null;
  }
  return null;
};

const DialogoPrograma = ({ open, onClose, programa }) => {
  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const watchNumFuncsRule = watch('numFuncsRule');
  const watchEmissoesRule = watch('emissoesRule');
  const watchEnergiaRule = watch('energiaRule');
  const watchFaturamentoRule = watch('faturamentoRule');

  React.useEffect(() => {
    if (programa) {
      reset({
        id: programa.id,
        nome: programa.nome,
        numFuncsRule: programa.num_funcs_rule ?? '',
        numFuncsValue: programa.num_funcs_value ?? '',
        emissoesRule: programa.emissoes_rule ?? '',
        emissoesValue: programa.emissoes_value ?? null,
        energiaRule: programa.energia_rule ?? '',
        energiaValue: programa.energia_value ?? null,
        faturamentoRule: programa.faturamento_rule ?? '',
        faturamentoValue: programa.faturamento_value ?? null,
        active: programa.active ?? true,
        visible: programa.visible ?? true,
      });
    } else {
      reset({
        nome: '',
        numFuncsRule: '',
        numFuncsValue: '',
        emissoesRule: '',
        emissoesValue: null,
        energiaRule: '',
        energiaValue: null,
        faturamentoRule: '',
        faturamentoValue: null,
        active: true,
        visible: true,
      });
    }
  }, [programa]);

  const salvar = async (data) => {
    const payload = {
      nome: data.nome,
      numFuncsRule: data.numFuncsRule || null,
      numFuncsValue: data.numFuncsRule ? (data.numFuncsValue || null) : null,
      emissoesRule: data.emissoesRule || null,
      emissoesValue: data.emissoesRule ? convertBrNumberToDb(data.emissoesValue) : null,
      energiaRule: data.energiaRule || null,
      energiaValue: data.energiaRule ? convertBrNumberToDb(data.energiaValue) : null,
      faturamentoRule: data.faturamentoRule || null,
      faturamentoValue: data.faturamentoRule ? convertBrNumberToDb(data.faturamentoValue) : null,
      active: data.active,
      visible: data.visible,
    };

    console.log('Payload final:', payload);

    try {
      if (programa?.id) {
        await api.http.put(`/programas/${programa.id}`, payload);
      } else {
        await api.http.post('/programas', payload);
      }
      onClose(true);
    } catch (error) {
      console.error('Erro ao salvar programa:', error);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} fullWidth maxWidth="sm">
      <DialogTitle>
        {programa?.id ? 'Editar Programa' : 'Novo Programa'}
      </DialogTitle>
      <form onSubmit={handleSubmit(salvar)}>
        <DialogContent>
          <Grid container spacing={1}>
            {/* Nome */}
            <Grid item xs={12}>
              <TextField
                label="Nome"
                {...register('nome')}
                error={!!errors.nome}
                helperText={errors.nome?.message}
                fullWidth
              />
            </Grid>

            {/* Funcionários */}
            <Grid item xs={6}>
              <Controller
                name="numFuncsRule"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField {...field} select label="Regra de funcionários" fullWidth margin="normal">
                    <MenuItem value="">Não se aplica</MenuItem>
                    <MenuItem value=">">Maior que</MenuItem>
                    <MenuItem value="<">Menor que</MenuItem>
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Qtdade de Funcionários"
                type="number"
                onKeyDown={(e) => {
                  if (e.key === '.' || e.key === ',') e.preventDefault();
                }}
                inputProps={{ min: 0, step: 1 }}
                {...register('numFuncsValue')}
                disabled={!watchNumFuncsRule}
                fullWidth
                margin="normal"
              />
            </Grid>

            {/* Emissões */}
            <Grid item xs={6}>
              <Controller
                name="emissoesRule"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField {...field} select label="Regra do potencial de emissões" fullWidth margin="normal">
                    <MenuItem value="">Não se aplica</MenuItem>
                    <MenuItem value=">">Maior que</MenuItem>
                    <MenuItem value="<">Menor que</MenuItem>
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="emissoesValue"
                control={control}
                defaultValue=""
                render={({ field: { onChange, ...rest } }) => (
                  <NumericFormat
                    {...rest}
                    customInput={TextField}
                    label="Total Emissões em tCO2/mês "
                    thousandSeparator="."
                    decimalSeparator=","
                    allowNegative={false}
                    decimalScale={2}
                    fixedDecimalScale
                    disabled={!watchEmissoesRule}
                    onValueChange={(values) => {
                      onChange(values.floatValue ?? null);
                    }}
                    fullWidth
                    margin="normal"
                  />
                )}
              />
            </Grid>

            {/* Energia */}
            <Grid item xs={6}>
              <Controller
                name="energiaRule"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField {...field} select label="Regra da fatura de energia" fullWidth margin="normal">
                    <MenuItem value="">Não se aplica</MenuItem>
                    <MenuItem value=">">Maior que</MenuItem>
                    <MenuItem value="<">Menor que</MenuItem>
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="energiaValue"
                control={control}
                defaultValue=""
                render={({ field: { onChange, ...rest } }) => (
                  <NumericFormat
                    {...rest}
                    customInput={TextField}
                    label="Total Energia em R$/mês"
                    thousandSeparator="."
                    decimalSeparator=","
                    allowNegative={false}
                    decimalScale={2}
                    fixedDecimalScale
                    disabled={!watchEnergiaRule}
                    onValueChange={(values) => {
                      onChange(values.floatValue ?? null);
                    }}
                    fullWidth
                    margin="normal"
                  />
                )}
              />
            </Grid>

            {/* Faturamento */}
            <Grid item xs={6}>
              <Controller
                name="faturamentoRule"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField {...field} select label="Regra de faturamento" fullWidth margin="normal">
                    <MenuItem value="">Não se aplica</MenuItem>
                    <MenuItem value=">">Maior que</MenuItem>
                    <MenuItem value="<">Menor que</MenuItem>
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="faturamentoValue"
                control={control}
                defaultValue=""
                render={({ field: { onChange, ...rest } }) => (
                  <NumericFormat
                    {...rest}
                    customInput={TextField}
                    label="Faturamento em R$"
                    thousandSeparator="."
                    decimalSeparator=","
                    allowNegative={false}
                    decimalScale={2}
                    fixedDecimalScale
                    disabled={!watchFaturamentoRule}
                    onValueChange={(values) => {
                      onChange(values.floatValue ?? null);
                    }}
                    fullWidth
                    margin="normal"
                  />
                )}
              />
            </Grid>

            {/* Status (checkbox) */}
            <Grid item xs={12}>
              <Controller
                name="active"
                control={control}
                defaultValue={true}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Programa ativo"
                  />
                )}
              />
            </Grid>
            {/* Visível na listagem (checkbox) */}
            <Grid item xs={12}>
              <Controller
                name="visible"
                control={control}
                defaultValue={true}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Visível na listagem"
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => onClose(false)} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            Salvar
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default DialogoPrograma;
